import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './marketing.css';

// API Base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_EP;

// Helper function to convert snake_case to camelCase
const snakeToCamel = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(snakeToCamel);
  }
  if (typeof obj === 'object') {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      newObj[camelKey] = snakeToCamel(obj[key]);
    });
    return newObj;
  }
  return obj;
};

// Helper function to convert camelCase to snake_case
const camelToSnake = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(camelToSnake);
  }
  if (typeof obj === 'object') {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      newObj[snakeKey] = camelToSnake(obj[key]);
    });
    return newObj;
  }
  return obj;
};

// API Service Functions
const segmentApi = {
  async getAll() {
    const response = await fetch(`${API_BASE_URL}/api/marketing/segments`);
    if (!response.ok) {
      throw new Error('Failed to fetch segments');
    }
    const data = await response.json();
    return snakeToCamel(data);
  },

  async create(name) {
    const response = await fetch(`${API_BASE_URL}/api/marketing/segments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create segment');
    }
    const data = await response.json();
    return snakeToCamel(data);
  },

  async update(id, name) {
    const response = await fetch(`${API_BASE_URL}/api/marketing/segments/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update segment');
    }
    const data = await response.json();
    return snakeToCamel(data);
  },

  async delete(id) {
    const response = await fetch(`${API_BASE_URL}/api/marketing/segments/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok && response.status !== 204) {
      throw new Error('Failed to delete segment');
    }
    return true;
  },
};

const taskApi = {
  async getAll(segmentId = null) {
    let url = `${API_BASE_URL}/api/marketing/tasks`;
    if (segmentId) {
      url += `?segmentId=${segmentId}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch tasks');
    }
    const data = await response.json();
    return snakeToCamel(data);
  },

  async getOne(id) {
    const response = await fetch(`${API_BASE_URL}/api/marketing/tasks/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch task');
    }
    const data = await response.json();
    return snakeToCamel(data);
  },

  async create(taskData) {
    const snakeCaseData = camelToSnake(taskData);
    const response = await fetch(`${API_BASE_URL}/api/marketing/tasks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(snakeCaseData),
    });
    if (!response.ok) {
      throw new Error('Failed to create task');
    }
    const data = await response.json();
    return snakeToCamel(data);
  },

  async update(id, taskData) {
    const snakeCaseData = camelToSnake(taskData);
    const response = await fetch(`${API_BASE_URL}/api/marketing/tasks/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(snakeCaseData),
    });
    if (!response.ok) {
      throw new Error('Failed to update task');
    }
    const data = await response.json();
    return snakeToCamel(data);
  },

  async delete(id) {
    const response = await fetch(`${API_BASE_URL}/api/marketing/tasks/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok && response.status !== 204) {
      throw new Error('Failed to delete task');
    }
    return true;
  },
};

// SVG Icons
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
);
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);
const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
);
const NoteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
);
const CheckCircleIcon = ({ completed }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={completed ? "#4caf50" : "none"} stroke={completed ? "#4caf50" : "#ccc"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
);
const ChevronDownIcon = ({ isOpen }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
);
const StatsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"></path><path d="M21 5c0 1.66-4 3-9 3S3 6.66 3 5s4-3 9-3 9 1.34 9 3Z"></path></svg>
);
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
);
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
);
const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
);
const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
);
const LoadingSpinner = () => (
  <svg className="marketing-board-spinner" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
  </svg>
);

// Maximum number of sections allowed
const MAX_SECTIONS = 10;

// Available colors for sections
const SECTION_COLORS = [
  '#4c63b6', '#e74c3c', '#27ae60', '#f39c12',
  '#9b59b6', '#1abc9c', '#e91e63', '#00bcd4',
];

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to format date for display
const formatDate = (dateString) => {
  if (!dateString) return 'No Date';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toISOString().split('T')[0];
};

// Helper function to get color for segment based on index
const getColorForIndex = (index) => {
  return SECTION_COLORS[index % SECTION_COLORS.length];
};

const Marketing = () => {
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Dynamic columns/sections state
  const [columns, setColumns] = useState({});

  // Track which columns have expanded completed sections
  const [expandedCompleted, setExpandedCompleted] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeColumnKey, setActiveColumnKey] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    quantity: '',
    startDate: '',
    endDate: '',
    note: '',
    priority: ''
  });

  // Section management state
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [editingSectionKey, setEditingSectionKey] = useState(null);
  const [sectionFormData, setSectionFormData] = useState({
    title: '',
    color: SECTION_COLORS[0]
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Date Filter State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
    isActive: false
  });
  const filterRef = useRef(null);

  // Stats Panel State
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [statsFilter, setStatsFilter] = useState('all');
  const [selectedAssignee, setSelectedAssignee] = useState(null);

  // Drag State
  const [dragState, setDragState] = useState({
    isDragging: false,
    draggedItem: null,
    sourceColumnKey: null,
    sourceIndex: null,
    targetIndex: null,
  });

  const dragRef = useRef({
    element: null,
    clone: null,
    placeholder: null,
    columnContent: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    scrollInterval: null,
    hasMoved: false,
    startTime: 0,
    pointerType: null,
    longPressTimer: null,
    longPressActive: false,
    cancelClick: false,
  });

  const containerRef = useRef(null);

  // ============ FETCH DATA ON MOUNT ============
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [segments, tasks] = await Promise.all([
        segmentApi.getAll(),
        taskApi.getAll()
      ]);

      const newColumns = {};
      const newExpandedCompleted = {};

      segments.forEach((segment, index) => {
        const segmentKey = segment.id.toString();
        newColumns[segmentKey] = {
          id: segment.id,
          title: segment.name,
          color: getColorForIndex(index),
          items: []
        };
        newExpandedCompleted[segmentKey] = false;
      });

      tasks.forEach(task => {
        const segmentKey = task.segmentId.toString();
        if (newColumns[segmentKey]) {
          newColumns[segmentKey].items.push({
            id: task.id,
            segmentId: task.segmentId,
            title: task.title || '',
            quantity: task.quantity || '',
            startDate: formatDate(task.startDate),
            endDate: formatDate(task.endDate),
            note: task.note || '',
            priority: task.priority || '',
            progress: task.progress || 0,
            completed: task.completed || false,
            completedAt: task.completedAt || null,
            createdAt: task.createdAt,
            updatedAt: task.updatedAt,
          });
        }
      });

      Object.keys(newColumns).forEach(key => {
        newColumns[key].items.sort((a, b) => {
          if (a.completed !== b.completed) {
            return a.completed ? 1 : -1;
          }
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
      });

      setColumns(newColumns);
      setExpandedCompleted(newExpandedCompleted);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Apply search and date filter to columns
  const filteredColumns = useMemo(() => {
    let filtered = {};

    Object.keys(columns).forEach(key => {
      filtered[key] = {
        ...columns[key],
        items: [...columns[key].items]
      };
    });

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filteredKeys = Object.keys(filtered).filter(key =>
        filtered[key].title.toLowerCase().includes(query)
      );

      const newFiltered = {};
      filteredKeys.forEach(key => {
        newFiltered[key] = filtered[key];
      });
      filtered = newFiltered;
    }

    if (dateFilter.isActive) {
      Object.keys(filtered).forEach(key => {
        filtered[key] = {
          ...filtered[key],
          items: filtered[key].items.filter(item => {
            if (!item.startDate || item.startDate === 'No Date') return true;

            const itemDate = new Date(item.startDate);
            const filterStart = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
            const filterEnd = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

            if (filterStart && filterEnd) {
              return itemDate >= filterStart && itemDate <= filterEnd;
            } else if (filterStart) {
              return itemDate >= filterStart;
            } else if (filterEnd) {
              return itemDate <= filterEnd;
            }
            return true;
          })
        };
      });
    }

    return filtered;
  }, [columns, dateFilter, searchQuery]);

  // Handle filter apply
  const handleApplyFilter = () => {
    if (dateFilter.startDate || dateFilter.endDate) {
      setDateFilter(prev => ({ ...prev, isActive: true }));
    }
    setIsFilterOpen(false);
  };

  // Handle filter clear
  const handleClearFilter = () => {
    setDateFilter({
      startDate: '',
      endDate: '',
      isActive: false
    });
    setIsFilterOpen(false);
  };

  // ============ SECTION MANAGEMENT ============

  const handleAddSectionClick = () => {
    if (Object.keys(columns).length >= MAX_SECTIONS) {
      alert(`Maximum of ${MAX_SECTIONS} sections allowed. Please delete an existing section to add a new one.`);
      return;
    }

    setEditingSectionKey(null);
    setSectionFormData({
      title: '',
      color: getColorForIndex(Object.keys(columns).length)
    });
    setIsSectionModalOpen(true);
  };

  const handleEditSectionClick = (e, columnKey) => {
    e.stopPropagation();
    const column = columns[columnKey];
    setEditingSectionKey(columnKey);
    setSectionFormData({
      title: column.title,
      color: column.color
    });
    setIsSectionModalOpen(true);
  };

  const handleSaveSection = async (e) => {
    e.preventDefault();
    if (!sectionFormData.title.trim()) {
      alert("Section title is required");
      return;
    }

    setIsSaving(true);

    try {
      if (editingSectionKey) {
        const updatedSegment = await segmentApi.update(
          columns[editingSectionKey].id,
          sectionFormData.title.trim()
        );

        setColumns(prev => ({
          ...prev,
          [editingSectionKey]: {
            ...prev[editingSectionKey],
            title: updatedSegment.name,
            color: sectionFormData.color
          }
        }));
      } else {
        const newSegment = await segmentApi.create(sectionFormData.title.trim());
        const newKey = newSegment.id.toString();

        setColumns(prev => ({
          ...prev,
          [newKey]: {
            id: newSegment.id,
            title: newSegment.name,
            color: sectionFormData.color,
            items: []
          }
        }));
        setExpandedCompleted(prev => ({
          ...prev,
          [newKey]: false
        }));
      }

      setIsSectionModalOpen(false);
      setEditingSectionKey(null);
    } catch (err) {
      console.error('Error saving section:', err);
      alert(err.message || 'Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSection = async (columnKey) => {
    const column = columns[columnKey];
    const hasItems = column.items && column.items.length > 0;

    const confirmMessage = hasItems
      ? `Are you sure you want to delete "${column.title}" section? This will also delete ${column.items.length} task(s) in it.`
      : `Are you sure you want to delete "${column.title}" section?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await segmentApi.delete(column.id);

      setColumns(prev => {
        const newColumns = { ...prev };
        delete newColumns[columnKey];
        return newColumns;
      });
      setExpandedCompleted(prev => {
        const newExpanded = { ...prev };
        delete newExpanded[columnKey];
        return newExpanded;
      });
    } catch (err) {
      console.error('Error deleting section:', err);
      alert('Failed to delete section. Please try again.');
    }
  };

  // ============ STATS CALCULATION ============

  const personStats = useMemo(() => {
    const stats = {};

    Object.entries(columns).forEach(([columnKey, column]) => {
      const personName = column.title;

      if (!stats[personName]) {
        stats[personName] = {
          name: personName,
          columnKey: columnKey,
          color: column.color,
          totalCompleted: 0,
          totalQuantity: 0,
          tasks: []
        };
      }

      column.items.forEach(item => {
        if (item.completed) {
          stats[personName].totalCompleted += 1;
          stats[personName].totalQuantity += parseInt(item.quantity) || 0;
          stats[personName].tasks.push({
            ...item,
            columnKey,
            columnTitle: column.title
          });
        }
      });
    });

    return Object.values(stats)
      .filter(person => person.totalCompleted > 0)
      .sort((a, b) => b.totalCompleted - a.totalCompleted);
  }, [columns]);

  const filteredStats = useMemo(() => {
    if (statsFilter === 'all') {
      return personStats;
    }

    return personStats.filter(person => person.columnKey === statsFilter);
  }, [personStats, statsFilter]);

  const totalStats = useMemo(() => {
    let total = 0;
    let totalQty = 0;

    if (statsFilter === 'all') {
      Object.values(columns).forEach(column => {
        column.items.forEach(item => {
          if (item.completed) {
            total += 1;
            totalQty += parseInt(item.quantity) || 0;
          }
        });
      });
    } else if (columns[statsFilter]) {
      columns[statsFilter].items.forEach(item => {
        if (item.completed) {
          total += 1;
          totalQty += parseInt(item.quantity) || 0;
        }
      });
    }

    return { total, totalQty };
  }, [columns, statsFilter]);

  const exportToCSV = () => {
    const headers = ['Person', 'Tasks Completed', 'Total Quantity', 'Task Details'];
    const rows = filteredStats.map(person => [
      person.name,
      person.totalCompleted,
      person.totalQuantity,
      person.tasks.map(t => `${t.title}`).join('; ')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `marketing-completed-tasks-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ============ TASK FUNCTIONS ============

  const getTasksByStatus = (items) => {
    const incomplete = items.filter(item => !item.completed);
    const completed = items.filter(item => item.completed);
    return { incomplete, completed };
  };

  const toggleCompletedSection = (columnKey) => {
    setExpandedCompleted(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const clearCompletedTasks = async (e, columnKey) => {
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to remove all completed tasks?')) {
      return;
    }

    const completedTasks = columns[columnKey].items.filter(item => item.completed);

    try {
      await Promise.all(
        completedTasks.map(task => taskApi.delete(task.id))
      );

      setColumns(prev => ({
        ...prev,
        [columnKey]: {
          ...prev[columnKey],
          items: prev[columnKey].items.filter(item => !item.completed)
        }
      }));
    } catch (err) {
      console.error('Error clearing completed tasks:', err);
      alert('Failed to clear completed tasks. Please try again.');
    }
  };

  // ============ DRAG AND DROP FUNCTIONS ============

  // handleAutoScroll - defined with useCallback FIRST
  const handleAutoScroll = useCallback((e, container) => {
    if (!container) return;

    const drag = dragRef.current;
    const rect = container.getBoundingClientRect();
    const scrollSpeed = 8;
    const threshold = 60;

    if (drag.scrollInterval) {
      clearInterval(drag.scrollInterval);
      drag.scrollInterval = null;
    }

    if (e.clientY < rect.top + threshold && container.scrollTop > 0) {
      drag.scrollInterval = setInterval(() => {
        container.scrollTop -= scrollSpeed;
      }, 16);
    } else if (e.clientY > rect.bottom - threshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight) {
      drag.scrollInterval = setInterval(() => {
        container.scrollTop += scrollSpeed;
      }, 16);
    }
  }, []);

  // handleEditClick - defined with useCallback
  const handleEditClick = useCallback((columnKey, task) => {
    setActiveColumnKey(columnKey);
    setEditingTaskId(task.id);
    setFormData({
      title: task.title || '',
      quantity: task.quantity || '',
      startDate: task.startDate !== 'No Date' ? task.startDate : '',
      endDate: task.endDate !== 'No Date' ? task.endDate : '',
      note: task.note || '',
      priority: task.priority || ''
    });
    setIsModalOpen(true);
  }, []);

  // cleanupDrag function
  const cleanupDrag = useCallback(() => {
    const drag = dragRef.current;

    if (drag.clone && drag.clone.parentNode) {
      drag.clone.parentNode.removeChild(drag.clone);
    }
    if (drag.placeholder && drag.placeholder.parentNode) {
      drag.placeholder.parentNode.removeChild(drag.placeholder);
    }
    if (drag.scrollInterval) {
      clearInterval(drag.scrollInterval);
    }
    if (drag.element) {
      drag.element.classList.remove('marketing-board-card-hidden');
    }
    if (drag.longPressTimer) {
      clearTimeout(drag.longPressTimer);
    }

    document.body.classList.remove('marketing-board-dragging');

    dragRef.current = {
      element: null,
      clone: null,
      placeholder: null,
      columnContent: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      scrollInterval: null,
      hasMoved: false,
      startTime: 0,
      pointerType: null,
      longPressTimer: null,
      longPressActive: false,
      cancelClick: false,
    };
  }, []);

  // initializeDrag function
  const initializeDrag = useCallback((e) => {
    const drag = dragRef.current;
    const cardElement = drag.element;
    const columnContent = drag.columnContent;

    if (!cardElement || !columnContent) return;

    const rect = cardElement.getBoundingClientRect();

    document.body.classList.add('marketing-board-dragging');

    const clone = cardElement.cloneNode(true);
    clone.classList.add('marketing-board-card-ghost');
    clone.classList.remove('marketing-board-card-hidden');
    clone.style.position = 'fixed';
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    clone.style.margin = '0';
    document.body.appendChild(clone);
    drag.clone = clone;

    const placeholder = document.createElement('div');
    placeholder.classList.add('marketing-board-card-placeholder');
    placeholder.style.height = `${rect.height}px`;
    cardElement.parentNode.insertBefore(placeholder, cardElement);
    drag.placeholder = placeholder;

    cardElement.classList.add('marketing-board-card-hidden');

    setDragState(prev => ({
      ...prev,
      isDragging: true,
    }));
  }, []);

  // handlePointerMove - defined with useCallback
  const handlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag.element) return;

    const isTouch = drag.pointerType === 'touch';

    const deltaX = Math.abs(e.clientX - drag.startX);
    const deltaY = Math.abs(e.clientY - drag.startY);

    // Touch: before long-press is active, treat movement as scroll, not drag
    if (isTouch && !drag.longPressActive) {
      if (deltaX > 5 || deltaY > 5) {
        if (drag.longPressTimer) {
          clearTimeout(drag.longPressTimer);
          drag.longPressTimer = null;
        }
        drag.cancelClick = true;
      }
      return;
    }

    // Start drag once pointer has moved a bit
    if (!drag.hasMoved && (deltaX > 5 || deltaY > 5)) {
      drag.hasMoved = true;
      initializeDrag(e);
    }

    if (!drag.clone) return;

    // While dragging on touch, prevent the page from scrolling
    if (isTouch) {
      e.preventDefault();
    }

    drag.clone.style.left = `${e.clientX - drag.offsetX}px`;
    drag.clone.style.top = `${e.clientY - drag.offsetY}px`;

    const columnContent = drag.columnContent;
    if (!columnContent) return;

    const cards = Array.from(
      columnContent.querySelectorAll('.marketing-board-card:not(.marketing-board-card-hidden)')
    );

    let targetIndex = 0;
    let insertBefore = null;

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const cardRect = card.getBoundingClientRect();
      const cardMiddle = cardRect.top + cardRect.height / 2;

      if (e.clientY < cardMiddle) {
        insertBefore = card;
        targetIndex = i;
        break;
      }
      targetIndex = i + 1;
    }

    if (cards.length > 0 && !insertBefore) {
      const firstCardRect = cards[0].getBoundingClientRect();
      if (e.clientY < firstCardRect.top) {
        insertBefore = cards[0];
        targetIndex = 0;
      }
    }

    if (insertBefore) {
      if (drag.placeholder.nextSibling !== insertBefore) {
        columnContent.insertBefore(drag.placeholder, insertBefore);
      }
    } else {
      if (drag.placeholder.parentNode === columnContent && drag.placeholder.nextSibling !== null) {
        columnContent.appendChild(drag.placeholder);
      } else if (drag.placeholder.parentNode !== columnContent) {
        columnContent.appendChild(drag.placeholder);
      }
    }

    setDragState(prev => ({
      ...prev,
      targetIndex: targetIndex,
    }));

    handleAutoScroll(e, columnContent.closest('.marketing-board-column-content'));
  }, [handleAutoScroll, initializeDrag]);

  // handlePointerUp - defined with useCallback AFTER handlePointerMove and handleEditClick
  const handlePointerUp = useCallback((e) => {
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
    document.removeEventListener('pointercancel', handlePointerUp);

    const drag = dragRef.current;

    if (drag.longPressTimer) {
      clearTimeout(drag.longPressTimer);
      drag.longPressTimer = null;
    }

    const timeDiff = drag.startTime ? Date.now() - drag.startTime : 0;

    // If the interaction was basically a scroll (mainly on touch), just clean up
    if (drag.cancelClick) {
      cleanupDrag();
      setDragState({
        isDragging: false,
        draggedItem: null,
        sourceColumnKey: null,
        sourceIndex: null,
        targetIndex: null,
      });
      return;
    }

    // Simple tap/click (no drag) -> open edit modal
    if (!drag.hasMoved || timeDiff < 150) {
      cleanupDrag();

      setDragState(prev => {
        if (prev.draggedItem && !drag.hasMoved) {
          setTimeout(() => {
            handleEditClick(prev.sourceColumnKey, prev.draggedItem);
          }, 0);
        }

        return {
          isDragging: false,
          draggedItem: null,
          sourceColumnKey: null,
          sourceIndex: null,
          targetIndex: null,
        };
      });
      return;
    }

    // Drag finished -> reorder within the same column
    setDragState(prev => {
      if (prev.isDragging && prev.draggedItem && prev.sourceIndex !== prev.targetIndex) {
        setColumns(cols => {
          const newColumns = { ...cols };
          const columnKey = prev.sourceColumnKey;

          const allItems = [...newColumns[columnKey].items];
          const incompleteItems = allItems.filter(item => !item.completed);
          const completedItems = allItems.filter(item => item.completed);

          const [draggedItem] = incompleteItems.splice(prev.sourceIndex, 1);

          let adjustedTargetIndex = prev.targetIndex;
          if (prev.sourceIndex < prev.targetIndex) {
            adjustedTargetIndex = prev.targetIndex - 1;
          }

          incompleteItems.splice(adjustedTargetIndex, 0, draggedItem);

          newColumns[columnKey] = {
            ...newColumns[columnKey],
            items: [...incompleteItems, ...completedItems],
          };

          return newColumns;
        });
      }

      return {
        isDragging: false,
        draggedItem: null,
        sourceColumnKey: null,
        sourceIndex: null,
        targetIndex: null,
      };
    });

    cleanupDrag();
  }, [handlePointerMove, handleEditClick, cleanupDrag]);

  // handlePointerDown
  const handlePointerDown = useCallback((e, columnKey, item, index) => {
    if (item.completed) return;
    if (e.target.closest('.marketing-board-complete-btn')) {
      return;
    }

    const isTouch = e.pointerType === 'touch';

    // For mouse we prevent default to avoid text selection.
    // For touch we let it be, so scrolling still works until long-press triggers.
    if (!isTouch) {
      e.preventDefault();
    }

    const cardElement = e.target.closest('.marketing-board-card');
    if (!cardElement) return;

    const rect = cardElement.getBoundingClientRect();
    const columnContent = cardElement.closest('.marketing-board-incomplete-section');
    if (!columnContent) return;

    const drag = dragRef.current;
    drag.element = cardElement;
    drag.columnContent = columnContent;
    drag.startX = e.clientX;
    drag.startY = e.clientY;
    drag.offsetX = e.clientX - rect.left;
    drag.offsetY = e.clientY - rect.top;
    drag.hasMoved = false;
    drag.startTime = Date.now();
    drag.pointerType = e.pointerType || 'mouse';
    drag.longPressActive = drag.pointerType !== 'touch'; // mouse: active immediately
    drag.cancelClick = false;

    // Touch: set up long-press timer (about 300ms) before drag can start
    if (drag.pointerType === 'touch') {
      drag.longPressTimer = window.setTimeout(() => {
        drag.longPressActive = true;
        // Add visual feedback for long-press
        if (cardElement) {
          cardElement.style.transform = 'scale(1.02)';
          cardElement.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
        }
      }, 300);
    }

    setDragState({
      isDragging: false,
      draggedItem: item,
      sourceColumnKey: columnKey,
      sourceIndex: index,
      targetIndex: index,
    });

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  }, [handlePointerMove, handlePointerUp]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      cleanupDrag();
    };
  }, [cleanupDrag]);

  const handleAddClick = (columnKey) => {
    if (dragState.isDragging) return;
    setActiveColumnKey(columnKey);
    setEditingTaskId(null);
    const today = getTodayDate();
    setFormData({
      title: '',
      quantity: '',
      startDate: today,
      endDate: today,
      note: '',
      priority: ''
    });
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'quantity') {
      const numValue = parseInt(value) || 0;
      if (numValue < 0) {
        setFormData({ ...formData, [name]: '0' });
        return;
      }
    }

    setFormData({ ...formData, [name]: value });
  };

  // Save handler with API integration
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return alert("Title is required");

    setIsSaving(true);

    try {
      const column = columns[activeColumnKey];

      const taskData = {
        segmentId: column.id,
        title: formData.title.trim(),
        quantity: formData.quantity ? parseInt(formData.quantity) : null,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        note: formData.note.trim() || null,
        priority: formData.priority || null,
        progress: 0,
        completed: false,
        completedAt: null
      };

      if (editingTaskId) {
        const existingTask = column.items.find(item => item.id === editingTaskId);
        taskData.completed = existingTask?.completed || false;
        taskData.completedAt = existingTask?.completedAt || null;

        await taskApi.update(editingTaskId, taskData);

        setColumns(prev => {
          const col = prev[activeColumnKey];
          const newItems = col.items.map(item =>
            item.id === editingTaskId
              ? {
                ...item,
                title: formData.title,
                quantity: formData.quantity,
                startDate: formData.startDate || 'No Date',
                endDate: formData.endDate || 'No Date',
                note: formData.note,
                priority: formData.priority
              }
              : item
          );

          return {
            ...prev,
            [activeColumnKey]: {
              ...col,
              items: newItems
            }
          };
        });
      } else {
        const newTask = await taskApi.create(taskData);

        const taskItem = {
          id: newTask.id,
          segmentId: newTask.segmentId,
          title: formData.title,
          quantity: formData.quantity,
          startDate: formData.startDate || 'No Date',
          endDate: formData.endDate || 'No Date',
          note: formData.note,
          priority: formData.priority,
          progress: 0,
          completed: false,
          completedAt: null,
          createdAt: newTask.createdAt
        };

        setColumns(prev => {
          const col = prev[activeColumnKey];
          const { incomplete, completed } = getTasksByStatus(col.items);

          return {
            ...prev,
            [activeColumnKey]: {
              ...col,
              items: [taskItem, ...incomplete, ...completed]
            }
          };
        });
      }

      setIsModalOpen(false);
      setEditingTaskId(null);
    } catch (err) {
      console.error('Error saving task:', err);
      alert('Failed to save task. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete task handler
  const handleDeleteTask = async () => {
    if (!editingTaskId) return;

    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    setIsSaving(true);

    try {
      await taskApi.delete(editingTaskId);

      setColumns(prev => {
        const column = prev[activeColumnKey];
        return {
          ...prev,
          [activeColumnKey]: {
            ...column,
            items: column.items.filter(item => item.id !== editingTaskId)
          }
        };
      });

      setIsModalOpen(false);
      setEditingTaskId(null);
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle completed with API integration
  const toggleCompleted = async (e, columnKey, taskId) => {
    e.stopPropagation();
    e.preventDefault();
    if (dragState.isDragging) return;

    const column = columns[columnKey];
    const task = column.items.find(item => item.id === taskId);
    if (!task) return;

    const newCompletedStatus = !task.completed;
    const completedAt = newCompletedStatus ? new Date().toISOString() : null;

    // Optimistically update UI
    setColumns(prev => {
      const col = prev[columnKey];
      const updatedItems = col.items.map(item => {
        if (item.id === taskId) {
          return {
            ...item,
            completed: newCompletedStatus,
            completedAt: completedAt
          };
        }
        return item;
      });

      const incomplete = updatedItems.filter(item => !item.completed);
      const completed = updatedItems.filter(item => item.completed);

      return {
        ...prev,
        [columnKey]: { ...col, items: [...incomplete, ...completed] }
      };
    });

    try {
      await taskApi.update(taskId, {
        segmentId: column.id,
        title: task.title,
        quantity: task.quantity ? parseInt(task.quantity) : null,
        startDate: task.startDate !== 'No Date' ? task.startDate : null,
        endDate: task.endDate !== 'No Date' ? task.endDate : null,
        note: task.note || null,
        priority: task.priority || null,
        progress: task.progress || 0,
        completed: newCompletedStatus,
        completedAt: completedAt
      });
    } catch (err) {
      console.error('Error updating task status:', err);
      // Revert on error
      setColumns(prev => {
        const col = prev[columnKey];
        const updatedItems = col.items.map(item => {
          if (item.id === taskId) {
            return {
              ...item,
              completed: !newCompletedStatus,
              completedAt: !newCompletedStatus ? null : task.completedAt
            };
          }
          return item;
        });

        const incomplete = updatedItems.filter(item => !item.completed);
        const completed = updatedItems.filter(item => item.completed);

        return {
          ...prev,
          [columnKey]: { ...col, items: [...incomplete, ...completed] }
        };
      });
      alert('Failed to update task status. Please try again.');
    }
  };

  const getPersonName = (columnKey) => {
    return columns[columnKey]?.title || 'Unknown';
  };

  const renderTaskCard = (item, columnKey, index, isCompleted = false) => {
    const personName = getPersonName(columnKey);

    return (
      <div
        key={item.id}
        className={`marketing-board-card ${isCompleted ? 'marketing-board-card-completed marketing-board-card-condensed' : ''}`}
        onPointerDown={!isCompleted ? (e) => handlePointerDown(e, columnKey, item, index) : undefined}
        onClick={isCompleted ? () => handleEditClick(columnKey, item) : undefined}
      >
        {isCompleted ? (
          <>
            <div className="marketing-board-card-condensed-content">
              <CheckCircleIcon completed={true} />
              <span className="marketing-board-card-condensed-title">{item.title}</span>
              <span className="marketing-board-card-condensed-qty">Qty: {item.quantity || '-'}</span>
            </div>
            <button
              className="marketing-board-complete-btn marketing-board-undo-btn"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => toggleCompleted(e, columnKey, item.id)}
              title="Mark as Incomplete"
            >
              ↩
            </button>
          </>
        ) : (
          <>
            <div className="marketing-board-card-badge">Qty: {item.quantity || '-'}</div>

            <h4 className="marketing-board-card-title">{item.title}</h4>

            {item.note && (
              <div className="marketing-board-card-note">
                <NoteIcon />
                <span className="marketing-board-note-text">{item.note}</span>
              </div>
            )}

            <div className="marketing-board-card-dates">
              <CalendarIcon />
              <span>{item.startDate || 'No Date'} - {item.endDate || 'No Date'}</span>
            </div>

            <div className="marketing-board-card-footer">
              <div className="marketing-board-assignee">
                <div className="marketing-board-avatar" style={{ backgroundColor: columns[columnKey]?.color }}>
                  {personName.charAt(0).toUpperCase()}
                </div>
                <span className="marketing-board-assignee-name">{personName}</span>
              </div>

              <button
                className="marketing-board-complete-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => toggleCompleted(e, columnKey, item.id)}
                title="Mark as Completed"
              >
                <CheckCircleIcon completed={false} />
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const maxCompleted = useMemo(() => {
    return Math.max(...filteredStats.map(a => a.totalCompleted), 1);
  }, [filteredStats]);

  const columnCount = Object.keys(columns).length;

  // Loading state
  if (isLoading) {
    return (
      <div className="marketing-board-container marketing-board-loading">
        <LoadingSpinner />
        <p>Loading marketing data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="marketing-board-container marketing-board-error">
        <p>{error}</p>
        <button onClick={fetchData} className="marketing-board-retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="marketing-board-container" ref={containerRef}>
      {/* Header */}
      <header className="marketing-board-header">
        {/* Top Row: Title and Action Buttons */}
        <div className="marketing-board-header-top">
          <div className="marketing-board-titles">
            <h1>Manage Marketing</h1>
            <div className="marketing-board-breadcrumbs">
              <span>Dashboard</span>
              <span className="marketing-board-chevron">&gt;</span>
              <span className="marketing-board-current">Marketing Tasks</span>
            </div>
          </div>

          <div className="marketing-board-header-actions">
            <button
              className="marketing-board-refresh-btn"
              onClick={fetchData}
              title="Refresh Data"
              disabled={isLoading}
            >
              <RefreshIcon />
            </button>

            <button
              className="marketing-board-add-section-btn"
              onClick={handleAddSectionClick}
              title={columnCount >= MAX_SECTIONS ? `Maximum ${MAX_SECTIONS} sections allowed` : "Add New Person"}
              disabled={columnCount >= MAX_SECTIONS}
            >
              <PlusIcon />
              <span>Add Person</span>
              {columnCount >= MAX_SECTIONS && (
                <span className="marketing-board-limit-badge">Max</span>
              )}
            </button>
          </div>
        </div>

        {/* Bottom Row: Search, Filter and Stats */}
        <div className="marketing-board-controls">
          <div className="marketing-board-search-bar">
            <span className="marketing-board-search-icon"><SearchIcon /></span>
            <input
              type="text"
              placeholder="Search Person..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="marketing-board-controls-right">
            <div className="marketing-board-filter-container" ref={filterRef}>
              <button
                className={`marketing-board-filter-btn ${dateFilter.isActive ? 'active' : ''}`}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                title="Filter by Date"
              >
                <FilterIcon />
                <span>Filter</span>
                {dateFilter.isActive && (
                  <span className="marketing-board-filter-badge">1</span>
                )}
              </button>

              {isFilterOpen && (
                <div className="marketing-board-filter-dropdown">
                  <div className="marketing-board-filter-header">
                    <h3>Filter by Start Date</h3>
                  </div>
                  <div className="marketing-board-filter-body">
                    <div className="marketing-board-filter-group">
                      <label>From Date</label>
                      <input
                        type="date"
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                    <div className="marketing-board-filter-group">
                      <label>To Date</label>
                      <input
                        type="date"
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="marketing-board-filter-footer">
                    <button
                      className="marketing-board-filter-clear-btn"
                      onClick={handleClearFilter}
                    >
                      Clear
                    </button>
                    <button
                      className="marketing-board-filter-apply-btn"
                      onClick={handleApplyFilter}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="marketing-board-stats-btn"
              onClick={() => setIsStatsOpen(true)}
              title="View Completion Stats"
            >
              <StatsIcon />
              <span>Stats</span>
              {totalStats.total > 0 && (
                <span className="marketing-board-stats-badge">{totalStats.total}</span>
              )}
            </button>
          </div>
        </div>

        {(columnCount > 0 || dateFilter.isActive) && (
          <div className="marketing-board-section-count">
            {columnCount > 0 && <span>{columnCount} / {MAX_SECTIONS} Persons</span>}
            {dateFilter.isActive && (
              <span className="marketing-board-filter-active-label">
                • Filtered: {dateFilter.startDate || 'Any'} to {dateFilter.endDate || 'Any'}
                <button
                  className="marketing-board-filter-clear-inline"
                  onClick={handleClearFilter}
                >
                  ✕
                </button>
              </span>
            )}
          </div>
        )}
      </header>

      {/* Board Area */}
      <div className="marketing-board-columns-container">
        {columnCount === 0 ? (
          <div className="marketing-board-empty-state-container">
            <div className="marketing-board-empty-state-content">
              <div className="marketing-board-empty-state-icon">
                <PlusIcon />
              </div>
              <h3>No Persons Yet</h3>
              <p>Create your first person to start organizing marketing tasks</p>
              <button
                className="marketing-board-empty-state-btn"
                onClick={handleAddSectionClick}
              >
                <PlusIcon />
                <span>Add First Person</span>
              </button>
            </div>
          </div>
        ) : (
          Object.entries(filteredColumns).map(([key, column]) => {
            const { incomplete, completed } = getTasksByStatus(column.items);
            const isExpanded = expandedCompleted[key];

            return (
              <div className="marketing-board-column" key={key} data-column-key={key}>
                <div className="marketing-board-column-header">
                  <div className="marketing-board-column-header-left">
                    <span className="marketing-board-dot" style={{ backgroundColor: column.color }}></span>
                    <h3>
                      {column.title}
                      <span className="marketing-board-count">({incomplete.length})</span>
                    </h3>
                  </div>
                  <div className="marketing-board-column-header-actions">
                    <button
                      className="marketing-board-edit-section-btn"
                      onClick={(e) => handleEditSectionClick(e, key)}
                      title="Edit Person"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="marketing-board-delete-section-btn"
                      onClick={() => handleDeleteSection(key)}
                      title="Delete Person"
                    >
                      <TrashIcon />
                    </button>
                    <button className="marketing-board-add-btn" onClick={() => handleAddClick(key)}>
                      <PlusIcon />
                    </button>
                  </div>
                </div>

                <div className="marketing-board-column-content">
                  <div className="marketing-board-incomplete-section">
                    {incomplete.map((item, index) => renderTaskCard(item, key, index, false))}

                    {incomplete.length === 0 && !dragState.isDragging && (
                      <div className="marketing-board-empty-state">
                        {dateFilter.isActive ? 'No tasks match filter' : 'No active tasks'}
                      </div>
                    )}
                  </div>

                  {completed.length > 0 && (
                    <div className="marketing-board-completed-section">
                      <div
                        className="marketing-board-completed-header"
                        onClick={() => toggleCompletedSection(key)}
                      >
                        <div className="marketing-board-completed-header-left">
                          <ChevronDownIcon isOpen={isExpanded} />
                          <span>Completed</span>
                          <span className="marketing-board-completed-count">{completed.length}</span>
                        </div>
                        <button
                          className="marketing-board-clear-completed-btn"
                          onClick={(e) => clearCompletedTasks(e, key)}
                          title="Clear all completed tasks"
                        >
                          <TrashIcon />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="marketing-board-completed-list">
                          {completed.map((item, index) => renderTaskCard(item, key, index, true))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats Panel */}
      {isStatsOpen && (
        <div className="marketing-board-stats-overlay">
          <div className="marketing-board-stats-panel">
            <div className="marketing-board-stats-header">
              {selectedAssignee ? (
                <>
                  <button
                    className="marketing-board-back-btn"
                    onClick={() => setSelectedAssignee(null)}
                  >
                    <BackIcon />
                  </button>
                  <h2>{selectedAssignee.name}'s Completed Tasks</h2>
                </>
              ) : (
                <h2>Completion Stats by Person</h2>
              )}
              <button
                className="marketing-board-close-btn"
                onClick={() => {
                  setIsStatsOpen(false);
                  setSelectedAssignee(null);
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {!selectedAssignee && (
              <div className="marketing-board-stats-actions">
                <div className="marketing-board-stats-filter">
                  <label>Filter by Person:</label>
                  <select
                    value={statsFilter}
                    onChange={(e) => setStatsFilter(e.target.value)}
                  >
                    <option value="all">All Persons</option>
                    {Object.entries(columns).map(([key, col]) => (
                      <option key={key} value={key}>{col.title}</option>
                    ))}
                  </select>
                </div>
                <button
                  className="marketing-board-export-btn"
                  onClick={exportToCSV}
                  title="Export to CSV"
                >
                  <DownloadIcon />
                  <span>Export</span>
                </button>
              </div>
            )}

            {!selectedAssignee && (
              <div className="marketing-board-stats-summary">
                <div className="marketing-board-summary-card">
                  <span className="marketing-board-summary-value">{totalStats.total}</span>
                  <span className="marketing-board-summary-label">Tasks Completed</span>
                </div>
                <div className="marketing-board-summary-card">
                  <span className="marketing-board-summary-value">{totalStats.totalQty}</span>
                  <span className="marketing-board-summary-label">Total Quantity</span>
                </div>
                <div className="marketing-board-summary-card">
                  <span className="marketing-board-summary-value">{filteredStats.length}</span>
                  <span className="marketing-board-summary-label">Persons</span>
                </div>
              </div>
            )}

            <div className="marketing-board-stats-content">
              {selectedAssignee ? (
                <div className="marketing-board-assignee-detail">
                  <div className="marketing-board-assignee-detail-header">
                    <div
                      className="marketing-board-detail-avatar"
                      style={{ backgroundColor: selectedAssignee.color }}
                    >
                      {selectedAssignee.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="marketing-board-detail-info">
                      <h3>{selectedAssignee.name}</h3>
                      <p>{selectedAssignee.totalCompleted} tasks completed • {selectedAssignee.totalQuantity} total quantity</p>
                    </div>
                  </div>

                  <div className="marketing-board-detail-tasks">
                    <h4>Completed Tasks</h4>
                    {selectedAssignee.tasks.map((task, idx) => (
                      <div key={idx} className="marketing-board-detail-task-item">
                        <div className="marketing-board-detail-task-main">
                          <CheckCircleIcon completed={true} />
                          <div className="marketing-board-detail-task-info">
                            <span className="marketing-board-detail-task-title">{task.title}</span>
                            <span className="marketing-board-detail-task-meta">
                              Qty: {task.quantity || '-'}
                            </span>
                          </div>
                        </div>
                        {task.endDate && task.endDate !== 'No Date' && (
                          <span className="marketing-board-detail-task-date">
                            <CalendarIcon /> {task.endDate}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {filteredStats.length === 0 ? (
                    <div className="marketing-board-stats-empty">
                      <p>No completed tasks found</p>
                      <span>Complete some tasks to see stats here</span>
                    </div>
                  ) : (
                    <div className="marketing-board-assignee-list">
                      {filteredStats.map((person, idx) => (
                        <div
                          key={idx}
                          className="marketing-board-assignee-row"
                          onClick={() => setSelectedAssignee(person)}
                        >
                          <div className="marketing-board-assignee-row-left">
                            <div
                              className="marketing-board-assignee-row-avatar"
                              style={{ backgroundColor: person.color }}
                            >
                              {person.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="marketing-board-assignee-row-info">
                              <span className="marketing-board-assignee-row-name">
                                {person.name}
                              </span>
                              <span className="marketing-board-assignee-row-meta">
                                {person.totalCompleted} tasks • {person.totalQuantity} qty
                              </span>
                            </div>
                          </div>

                          <div className="marketing-board-assignee-row-right">
                            <div className="marketing-board-progress-bar-container">
                              <div
                                className="marketing-board-progress-bar-fill"
                                style={{
                                  width: `${(person.totalCompleted / maxCompleted) * 100}%`,
                                  backgroundColor: person.color
                                }}
                              ></div>
                            </div>
                            <span className="marketing-board-assignee-row-count">
                              {person.totalCompleted}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section Modal (Add/Edit Person) */}
      {isSectionModalOpen && (
        <div className="marketing-board-modal-overlay">
          <div className="marketing-board-modal-content marketing-board-section-modal">
            <div className="marketing-board-modal-header">
              <h2>{editingSectionKey ? 'Edit Person' : 'Add New Person'}</h2>
              <button
                className="marketing-board-close-btn"
                onClick={() => setIsSectionModalOpen(false)}
                disabled={isSaving}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveSection}>
              <div className="marketing-board-form-group">
                <label>Person Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={sectionFormData.title}
                  onChange={(e) => setSectionFormData({ ...sectionFormData, title: e.target.value })}
                  placeholder="Enter person's name"
                  required
                  autoFocus
                  disabled={isSaving}
                />
              </div>
              <div className="marketing-board-form-group">
                <label>Color</label>
                <div className="marketing-board-color-picker">
                  {SECTION_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`marketing-board-color-option ${sectionFormData.color === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setSectionFormData({ ...sectionFormData, color })}
                      title={color}
                      disabled={isSaving}
                    />
                  ))}
                </div>
              </div>

              <div className="marketing-board-modal-footer">
                <button
                  type="button"
                  className="marketing-board-cancel-btn"
                  onClick={() => setIsSectionModalOpen(false)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="marketing-board-save-btn"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : (editingSectionKey ? 'Update Person' : 'Create Person')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {isModalOpen && (
        <div className="marketing-board-modal-overlay">
          <div className="marketing-board-modal-content">
            <div className="marketing-board-modal-header">
              <h2>{editingTaskId ? 'Edit Task' : `Add Task for ${columns[activeColumnKey]?.title}`}</h2>
              <button
                className="marketing-board-close-btn"
                onClick={() => setIsModalOpen(false)}
                disabled={isSaving}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="marketing-board-form-group">
                <label>Title <span className="required">*</span></label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g. Create new task"
                  required
                  disabled={isSaving}
                />
              </div>
              <div className="marketing-board-form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  placeholder="e.g. 5"
                  min="0"
                  disabled={isSaving}
                />
              </div>

              <div className="marketing-board-form-group">
                <label>Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  disabled={isSaving}
                >
                  <option value="">Select Priority</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="marketing-board-form-group">
                <label>Note</label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  placeholder="Add any additional notes or comments here..."
                  rows="3"
                  className="marketing-board-textarea"
                  disabled={isSaving}
                />
              </div>

              <div className="marketing-board-form-row">
                <div className="marketing-board-form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    disabled={isSaving}
                  />
                </div>
                <div className="marketing-board-form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="marketing-board-form-info">
                <span className="marketing-board-form-info-label">Assigned to:</span>
                <span className="marketing-board-form-info-value">
                  {columns[activeColumnKey]?.title}
                </span>
              </div>

              <div className="marketing-board-modal-footer">
                {editingTaskId && (
                  <button
                    type="button"
                    className="marketing-board-delete-btn"
                    onClick={handleDeleteTask}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Deleting...' : 'Delete'}
                  </button>
                )}
                <div className="marketing-board-modal-footer-right">
                  <button
                    type="button"
                    className="marketing-board-cancel-btn"
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="marketing-board-save-btn"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : (editingTaskId ? 'Update Task' : 'Create Task')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marketing;
